import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Plus, Star, Quote, Truck, RefreshCw, Shield, Clock, Instagram, Sparkles, ChevronRight, Play, Pause, MessageCircle, Share2, TrendingUp, Music, Volume2, VolumeX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 5: MOTION / VOGUE
 * Mobile: Editorial, Magazine-style (VOGUE)
 * Desktop: Dynamic, Energetic, Youth-focused (MOTION)
 */
const Template5 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);
    const [isMuted, setIsMuted] = useState(true);
    const videoRef = useRef(null);

    // Auto-rotate testimonials (mobile)
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) =>
                diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length
            );
        }
    };

    const toggleWishlist = (id, e) => {
        e.stopPropagation();
        setWishlist((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    // Mobile data (VOGUE)
    const categories = [
        { name: 'The Edit', count: '120+ Pieces', image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=600&h=800&fit=crop' },
        { name: 'Runway', count: '85+ Looks', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=600&h=800&fit=crop' },
        { name: 'Evening Wear', count: '60+ Gowns', image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=600&h=800&fit=crop' },
    ];

    const products = [
        { id: 1, name: 'Silk Drape Gown', price: 18999, originalPrice: 24999, badge: 'NEW', image: 'https://images.unsplash.com/photo-1539008835657-9e8e9680c956?w=500&h=650&fit=crop' },
        { id: 2, name: 'Structured Blazer Dress', price: 12499, originalPrice: null, badge: 'TRENDING', image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=500&h=650&fit=crop' },
        { id: 3, name: 'Asymmetric Midi Skirt', price: 7999, originalPrice: 10999, badge: 'SALE', image: 'https://images.unsplash.com/photo-1485968579580-b6d095142e6e?w=500&h=650&fit=crop' },
        { id: 4, name: 'Embellished Column Dress', price: 22999, originalPrice: null, badge: 'BESTSELLER', image: 'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=500&h=650&fit=crop' },
        { id: 5, name: 'Pleated Palazzo Set', price: 9499, originalPrice: 13999, badge: 'SALE', image: 'https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=500&h=650&fit=crop' },
        { id: 6, name: 'Tailored Jumpsuit', price: 14999, originalPrice: null, badge: 'NEW', image: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=500&h=650&fit=crop' },
    ];

    const testimonials = [
        { text: 'The editorial quality of these garments is unmatched. Every piece feels like it belongs on a magazine cover.', name: 'Simone D\'Souza', location: 'Mumbai', rating: 5 },
        { text: 'I wore the silk drape gown to a gala and received compliments all evening. The construction is impeccable.', name: 'Natasha Gill', location: 'Delhi', rating: 5 },
        { text: 'Finally, a brand that understands high fashion without the prohibitive couture pricing. Absolutely stunning.', name: 'Priya Menon', location: 'Bangalore', rating: 5 },
    ];

    const instagramImages = [
        'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop',
    ];

    // Desktop data (MOTION)
    const marqueeText = "NEW DROPS EVERY FRIDAY \u2022 FREE SHIPPING WORLDWIDE \u2022 TRENDING NOW \u2022 ";

    return (
        <div className="min-h-screen overflow-x-hidden font-sans">

            {/* ========== MOBILE WRAPPER ========== */}
            <div className="lg:hidden bg-white text-neutral-900">

                {/* ANNOUNCEMENT BAR - Mobile */}
                <div className="bg-neutral-900 text-white py-2.5 text-center">
                    <p className="text-[11px] sm:text-xs tracking-[0.2em] uppercase font-light">Complimentary Shipping on Orders Above Rs.5,000 &bull; New Editorial Drop Every Friday</p>
                </div>

                {/* HERO - Mobile Stacked */}
                <section>
                    <div className="relative aspect-[4/5]">
                        <img src="https://images.unsplash.com/photo-1509631179647-0177331693ae?w=1920&q=80" alt="Vogue Editorial" className="w-full h-full object-cover" />
                    </div>
                    <div className="bg-white px-5 py-8 text-center">
                        <span className="text-[11px] tracking-[0.3em] uppercase text-neutral-400 font-light">Spring/Summer 2026</span>
                        <h1 className="text-2xl sm:text-3xl font-extralight tracking-tight text-neutral-900 mt-2 mb-3 leading-tight">
                            The New <span className="italic font-serif">Editorial</span>
                        </h1>
                        <p className="text-[14px] text-neutral-500 mb-6 max-w-sm mx-auto leading-relaxed">
                            Fashion-forward silhouettes crafted for the woman who sets the standard, not follows it.
                        </p>
                        <button
                            onClick={() => navigate('/template5/shop')}
                            className="w-full bg-neutral-900 text-white py-4 text-sm tracking-[0.15em] uppercase font-medium min-h-[48px]"
                        >
                            Explore the Collection
                        </button>
                    </div>
                </section>

                {/* CATEGORIES - Mobile horizontal scroll */}
                <section className="py-10 sm:py-16 bg-neutral-50">
                    <div className="px-5 sm:px-6 mb-6">
                        <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-neutral-400">Curated For You</span>
                        <h2 className="text-xl sm:text-2xl font-extralight text-neutral-900 mt-1">Shop the Edit</h2>
                    </div>
                    <div className="relative">
                        <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                            {categories.map((cat, i) => (
                                <div key={i} onClick={() => navigate('/template5/shop')} className="flex-none w-[68vw] snap-start cursor-pointer group">
                                    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden">
                                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                                        <div className="absolute bottom-4 left-4 right-4">
                                            <h3 className="text-white text-lg font-medium">{cat.name}</h3>
                                            <p className="text-white/70 text-xs mt-0.5">{cat.count}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="absolute right-0 top-0 bottom-4 w-12 bg-gradient-to-l from-neutral-50 to-transparent pointer-events-none" />
                    </div>
                </section>

                {/* PRODUCTS - Mobile horizontal scroll */}
                <section className="py-10 sm:py-16 bg-white">
                    <div className="px-5 sm:px-6 flex items-end justify-between mb-6">
                        <div>
                            <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-neutral-400">The Collection</span>
                            <h2 className="text-xl sm:text-2xl font-extralight text-neutral-900 mt-1">New Arrivals</h2>
                        </div>
                    </div>
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                        {products.map((product) => (
                            <div key={product.id} onClick={() => navigate('/template5/shop')} className="flex-none w-[44vw] snap-start cursor-pointer group">
                                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-2 bg-neutral-100">
                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                    {product.badge && (
                                        <span className={`absolute top-2 left-2 px-2 py-0.5 text-[10px] font-semibold tracking-wide rounded-full ${product.badge === 'SALE' ? 'bg-red-500 text-white' : product.badge === 'NEW' ? 'bg-neutral-900 text-white' : 'bg-amber-400 text-neutral-900'}`}>
                                            {product.badge}
                                        </span>
                                    )}
                                    <button onClick={(e) => toggleWishlist(product.id, e)} className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                                        <Heart className={`w-3.5 h-3.5 ${wishlist[product.id] ? 'fill-red-500 text-red-500' : 'text-neutral-600'}`} />
                                    </button>
                                    <button className="absolute bottom-2 right-2 w-8 h-8 bg-neutral-900/90 rounded-full flex items-center justify-center">
                                        <Plus className="w-3.5 h-3.5 text-white" />
                                    </button>
                                </div>
                                <h3 className="text-[13px] font-medium text-neutral-900 line-clamp-1">{product.name}</h3>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[13px] font-semibold">Rs.{product.price.toLocaleString()}</span>
                                    {product.originalPrice && <span className="text-[11px] text-neutral-400 line-through">Rs.{product.originalPrice.toLocaleString()}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="px-5 mt-4">
                        <button onClick={() => navigate('/template5/shop')} className="w-full py-3.5 border border-neutral-900 text-neutral-900 text-sm tracking-[0.1em] uppercase font-medium min-h-[48px]">
                            View All Products
                        </button>
                    </div>
                </section>

                {/* BRAND STORY - Mobile */}
                <section className="py-10 sm:py-16 bg-neutral-50">
                    <div>
                        <div className="sm:rounded-2xl sm:mx-6 overflow-hidden">
                            <img src="https://images.unsplash.com/photo-1469334031218-e382a71b716b?w=800&h=1000&fit=crop" alt="Atelier" className="w-full aspect-[4/5] object-cover" />
                        </div>
                        <div className="px-5 sm:px-6 py-8">
                            <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-neutral-400">Our Story</span>
                            <h2 className="text-xl sm:text-2xl font-extralight text-neutral-900 mt-2 mb-4 leading-snug">
                                Where Fashion Meets <span className="italic font-serif">Art</span>
                            </h2>
                            <p className="text-[14px] sm:text-base text-neutral-600 leading-relaxed mb-4">
                                Born from a deep reverence for craftsmanship and modern design, VOGUE creates editorial fashion
                                that transcends seasons. Each collection is a conversation between heritage techniques and contemporary vision.
                            </p>
                            <p className="text-[14px] sm:text-base text-neutral-500 leading-relaxed mb-6">
                                Our atelier works with master artisans and emerging talents, producing limited-edition pieces
                                that are as thoughtfully made as they are breathtakingly beautiful.
                            </p>
                            <div className="flex gap-8 py-6 border-y border-neutral-200 mb-6">
                                <div><p className="text-2xl font-light text-neutral-900">12+</p><p className="text-xs text-neutral-500 mt-0.5">Years</p></div>
                                <div><p className="text-2xl font-light text-neutral-900">200+</p><p className="text-xs text-neutral-500 mt-0.5">Artisans</p></div>
                                <div><p className="text-2xl font-light text-neutral-900">50k+</p><p className="text-xs text-neutral-500 mt-0.5">Clients</p></div>
                            </div>
                            <button onClick={() => navigate('/template5/shop')} className="group inline-flex items-center gap-2 text-neutral-900 text-sm font-medium hover:text-neutral-600 transition-colors">
                                Read More <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </section>

                {/* TESTIMONIALS - Mobile swipeable */}
                <section className="py-10 sm:py-16 bg-white">
                    <div className="px-5 sm:px-6">
                        <div className="text-center mb-6">
                            <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-neutral-400">Client Voices</span>
                            <h2 className="text-xl sm:text-2xl font-extralight text-neutral-900 mt-1">What They Say</h2>
                        </div>
                        <div className="overflow-hidden" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                            <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                                {testimonials.map((t, i) => (
                                    <div key={i} className="w-full flex-none px-1">
                                        <div className="bg-neutral-50 rounded-2xl p-6">
                                            <div className="flex gap-0.5 mb-3">{[...Array(t.rating)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                                            <p className="text-[14px] text-neutral-700 leading-relaxed mb-4 italic">"{t.text}"</p>
                                            <div className="border-t border-neutral-200 pt-3">
                                                <p className="text-sm font-medium text-neutral-900">{t.name}</p>
                                                <p className="text-xs text-neutral-500">{t.location}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-center gap-2 mt-4">
                            {testimonials.map((_, i) => (
                                <button key={i} onClick={() => setActiveTestimonial(i)} className={`h-1.5 rounded-full transition-all duration-300 ${activeTestimonial === i ? 'w-6 bg-neutral-900' : 'w-1.5 bg-neutral-300'}`} />
                            ))}
                        </div>
                    </div>
                </section>

                {/* FEATURES - Mobile horizontal scroll */}
                <section className="py-10 sm:py-16 bg-neutral-50">
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-2">
                        {[
                            { icon: Truck, title: 'Complimentary Shipping', desc: 'On all orders above Rs.5,000' },
                            { icon: RefreshCw, title: 'Easy Returns', desc: '15-day hassle-free returns' },
                            { icon: Shield, title: 'Authenticity Guaranteed', desc: 'Genuine designer pieces' },
                            { icon: Clock, title: 'Personal Styling', desc: 'Expert fashion consultation' },
                        ].map((feature, i) => (
                            <div key={i} className="flex-none w-[55vw] snap-start flex items-start gap-3 bg-white rounded-2xl p-4">
                                <div className="w-10 h-10 bg-neutral-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <feature.icon className="w-4 h-4 text-neutral-700" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-neutral-900">{feature.title}</p>
                                    <p className="text-xs text-neutral-500 mt-0.5">{feature.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* NEWSLETTER - Mobile */}
                <section className="py-10 sm:py-16 bg-neutral-900 text-white">
                    <div className="px-5 sm:px-6 text-center">
                        <Sparkles className="w-6 h-6 text-neutral-400 mx-auto mb-4" />
                        <h2 className="text-xl sm:text-2xl font-extralight mb-3">Join the Inner Circle</h2>
                        <p className="text-[14px] sm:text-base text-neutral-400 mb-6">
                            Receive exclusive previews, editorial content, and 10% off your first order.
                        </p>
                        <form className="flex flex-col gap-3 max-w-md mx-auto">
                            <input
                                type="email"
                                placeholder="your@email.com"
                                className="flex-1 px-5 py-3.5 bg-white/10 border border-white/20 rounded-xl text-white placeholder-neutral-500 focus:outline-none focus:border-white/50 transition-colors"
                            />
                            <button type="submit" className="px-8 py-3.5 bg-white text-neutral-900 text-sm tracking-[0.1em] uppercase font-medium rounded-xl hover:bg-neutral-200 transition-colors min-h-[48px]">
                                Subscribe
                            </button>
                        </form>
                        <p className="text-neutral-600 text-[11px] sm:text-xs mt-4">No spam. Unsubscribe anytime.</p>
                    </div>
                </section>

                {/* INSTAGRAM - Mobile 3-col */}
                <section className="py-10 sm:py-16 bg-white">
                    <div className="text-center mb-6 px-5">
                        <div className="flex items-center justify-center gap-2 mb-1">
                            <Instagram className="w-5 h-5 text-neutral-700" />
                            <span className="text-sm text-neutral-700 font-medium">@vogueatelier</span>
                        </div>
                        <h2 className="text-xl sm:text-2xl font-extralight text-neutral-900">Follow Our World</h2>
                    </div>
                    <div className="grid grid-cols-3 gap-0.5">
                        {instagramImages.map((img, i) => (
                            <div key={i} className="relative aspect-square overflow-hidden group cursor-pointer">
                                <img src={img} alt={`Post ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

            </div>

            {/* ========== DESKTOP WRAPPER (ORIGINAL MOTION THEME) ========== */}
            <div className="hidden lg:block bg-black text-white">

                {/* MARQUEE BANNER */}
                <div className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-pink-500 py-2 overflow-hidden">
                    <div className="animate-marquee whitespace-nowrap flex">
                        {[...Array(4)].map((_, i) => (
                            <span key={i} className="text-sm font-bold uppercase tracking-wider mx-4">
                                {marqueeText}
                            </span>
                        ))}
                    </div>
                </div>

                {/* HERO - Desktop full-screen video */}
                <section className="relative h-screen flex items-center justify-center overflow-hidden">
                    <div className="absolute inset-0">
                        <video
                            ref={videoRef}
                            autoPlay
                            loop
                            muted={isMuted}
                            playsInline
                            className="w-full h-full object-cover"
                            poster="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80"
                        >
                            <source src="https://assets.mixkit.co/videos/preview/mixkit-young-woman-looking-at-the-city-through-the-window-4813-large.mp4" type="video/mp4" />
                        </video>
                        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black"></div>
                    </div>
                    <button
                        onClick={() => setIsMuted(!isMuted)}
                        className="absolute top-4 right-4 z-20 w-12 h-12 bg-white/10 backdrop-blur-md rounded-full flex items-center justify-center hover:bg-white/20 transition-colors"
                    >
                        {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                    <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
                        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                            <Sparkles className="w-4 h-4 text-yellow-400" />
                            <span className="text-sm font-medium">New Collection Dropped</span>
                        </div>
                        <h1 className="text-5xl sm:text-7xl lg:text-9xl font-black uppercase leading-none mb-6 bg-gradient-to-r from-white via-fuchsia-200 to-violet-200 bg-clip-text text-transparent">
                            BE BOLD
                        </h1>
                        <p className="text-xl sm:text-2xl text-white/80 mb-8 max-w-2xl mx-auto">
                            Fashion that moves with you. Express yourself, break the rules.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <button
                                onClick={() => navigate('/template5/shop')}
                                className="group inline-flex items-center gap-3 bg-white text-black px-8 py-4 font-bold uppercase tracking-wide hover:bg-fuchsia-500 hover:text-white transition-all duration-300"
                            >
                                Shop Now
                                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </button>
                            <button
                                onClick={() => navigate('/template5/shop')}
                                className="inline-flex items-center gap-3 border-2 border-white/50 text-white px-8 py-4 font-bold uppercase tracking-wide hover:bg-white hover:text-black transition-all duration-300"
                            >
                                <Play className="w-5 h-5" fill="currentColor" />
                                Watch Film
                            </button>
                        </div>
                    </div>
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
                        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
                            <div className="w-1.5 h-3 bg-white rounded-full animate-pulse"></div>
                        </div>
                    </div>
                </section>

                {/* TRENDING NOW - Desktop horizontal scroll */}
                <section className="py-16 sm:py-20 bg-black">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between mb-10">
                            <div className="flex items-center gap-3">
                                <TrendingUp className="w-6 h-6 text-fuchsia-500" />
                                <h2 className="text-2xl sm:text-3xl font-black uppercase">Trending Now</h2>
                            </div>
                            <button
                                onClick={() => navigate('/template5/shop')}
                                className="text-fuchsia-500 text-sm font-bold uppercase tracking-wide hover:text-fuchsia-400 transition-colors"
                            >
                                View All
                            </button>
                        </div>
                        <div className="flex overflow-x-auto gap-4 pb-4 -mx-4 px-4 scrollbar-hide snap-x snap-mandatory">
                            {[
                                { name: 'Oversized Hoodie', price: '\u20b92,499', tag: 'HOT', image: 'https://images.unsplash.com/photo-1556821840-3a63f95609a7?w=400&h=500&fit=crop', color: 'from-violet-600 to-fuchsia-600' },
                                { name: 'Cargo Pants', price: '\u20b93,299', tag: 'VIRAL', image: 'https://images.unsplash.com/photo-1624378439575-d8705ad7ae80?w=400&h=500&fit=crop', color: 'from-pink-600 to-orange-500' },
                                { name: 'Graphic Tee', price: '\u20b91,499', tag: 'NEW', image: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?w=400&h=500&fit=crop', color: 'from-cyan-500 to-blue-600' },
                                { name: 'Platform Sneakers', price: '\u20b94,999', tag: 'SOLD OUT', image: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=400&h=500&fit=crop', color: 'from-green-500 to-emerald-600' },
                                { name: 'Chain Necklace', price: '\u20b9999', tag: 'TRENDING', image: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=400&h=500&fit=crop', color: 'from-amber-500 to-yellow-500' }
                            ].map((item, index) => (
                                <div
                                    key={index}
                                    onClick={() => navigate('/template5/shop')}
                                    className="flex-none w-64 sm:w-72 group cursor-pointer snap-start"
                                >
                                    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-4">
                                        <img src={item.image} alt={item.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                        <div className={`absolute inset-0 bg-gradient-to-t ${item.color} opacity-0 group-hover:opacity-60 transition-opacity duration-300`}></div>
                                        <div className={`absolute top-3 left-3 bg-gradient-to-r ${item.color} text-white text-xs font-bold px-3 py-1 rounded-full`}>{item.tag}</div>
                                        <div className="absolute bottom-3 left-3 right-3 flex justify-between opacity-0 group-hover:opacity-100 translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                                            <button className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-black hover:bg-fuchsia-500 hover:text-white transition-colors">
                                                <Heart className="w-5 h-5" />
                                            </button>
                                            <button className="flex-1 mx-2 bg-white text-black py-2 font-bold text-sm uppercase tracking-wide hover:bg-fuchsia-500 hover:text-white transition-colors rounded-full">
                                                Add to Bag
                                            </button>
                                        </div>
                                    </div>
                                    <h3 className="font-bold text-white mb-1 group-hover:text-fuchsia-400 transition-colors">{item.name}</h3>
                                    <p className="text-white/60">{item.price}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* CATEGORIES - Desktop bold grid */}
                <section className="py-16 sm:py-20 bg-zinc-950">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-3xl sm:text-4xl font-black uppercase text-center mb-12">
                            Shop By <span className="bg-gradient-to-r from-fuchsia-500 to-violet-500 bg-clip-text text-transparent">Vibe</span>
                        </h2>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                            {[
                                { name: 'Streetwear', count: '120+ Items', image: 'https://images.unsplash.com/photo-1523398002811-999ca8dec234?w=400&h=500&fit=crop', gradient: 'from-violet-600/80 to-transparent' },
                                { name: 'Y2K', count: '85+ Items', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=400&h=500&fit=crop', gradient: 'from-pink-600/80 to-transparent' },
                                { name: 'Minimalist', count: '65+ Items', image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=400&h=500&fit=crop', gradient: 'from-zinc-600/80 to-transparent' },
                                { name: 'Sporty', count: '90+ Items', image: 'https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=400&h=500&fit=crop', gradient: 'from-cyan-600/80 to-transparent' }
                            ].map((category, index) => (
                                <div key={index} onClick={() => navigate('/template5/shop')} className="group relative aspect-[3/4] rounded-3xl overflow-hidden cursor-pointer">
                                    <img src={category.image} alt={category.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                    <div className={`absolute inset-0 bg-gradient-to-t ${category.gradient}`}></div>
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors"></div>
                                    <div className="absolute bottom-6 left-6 right-6">
                                        <h3 className="text-2xl sm:text-3xl font-black uppercase mb-1">{category.name}</h3>
                                        <p className="text-white/70 text-sm">{category.count}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* REELS/TIKTOK STYLE - Desktop */}
                <section className="py-16 sm:py-20 bg-black">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex items-center justify-between mb-10">
                            <div className="flex items-center gap-3">
                                <Music className="w-6 h-6 text-fuchsia-500" />
                                <h2 className="text-2xl sm:text-3xl font-black uppercase">Motion Reels</h2>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                            {[
                                { views: '2.5M', likes: '120K', image: 'https://images.unsplash.com/photo-1529139574466-a303027c1d8b?w=300&h=500&fit=crop' },
                                { views: '1.8M', likes: '95K', image: 'https://images.unsplash.com/photo-1485968579169-68d2e5d5d4a4?w=300&h=500&fit=crop' },
                                { views: '3.2M', likes: '180K', image: 'https://images.unsplash.com/photo-1496747611176-843222e1e57c?w=300&h=500&fit=crop' },
                                { views: '890K', likes: '45K', image: 'https://images.unsplash.com/photo-1509631179647-0177331693ae?w=300&h=500&fit=crop' },
                                { views: '1.2M', likes: '67K', image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=300&h=500&fit=crop' }
                            ].map((reel, index) => (
                                <div key={index} className="relative aspect-[9/16] rounded-2xl overflow-hidden group cursor-pointer">
                                    <img src={reel.image} alt={`Reel ${index + 1}`} className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                        <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
                                            <Play className="w-8 h-8 ml-1" fill="white" />
                                        </div>
                                    </div>
                                    <div className="absolute bottom-4 left-4 right-4">
                                        <div className="flex items-center gap-4 text-sm">
                                            <span className="flex items-center gap-1"><Play className="w-4 h-4" fill="white" /> {reel.views}</span>
                                            <span className="flex items-center gap-1"><Heart className="w-4 h-4" fill="white" /> {reel.likes}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* PRODUCTS - Desktop grid */}
                <section className="py-16 sm:py-20 bg-zinc-950">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl sm:text-4xl font-black uppercase mb-4">
                                Fresh <span className="bg-gradient-to-r from-fuchsia-500 to-violet-500 bg-clip-text text-transparent">Drops</span>
                            </h2>
                            <p className="text-white/60">New arrivals every week</p>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                            {[
                                { name: 'Puffer Jacket', price: '\u20b95,999', originalPrice: '\u20b97,999', image: 'https://images.unsplash.com/photo-1544022613-e87ca75a784a?w=400&h=500&fit=crop' },
                                { name: 'Wide Leg Jeans', price: '\u20b92,999', originalPrice: null, image: 'https://images.unsplash.com/photo-1541099649105-f69ad21f3246?w=400&h=500&fit=crop' },
                                { name: 'Crop Top', price: '\u20b91,299', originalPrice: '\u20b91,999', image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=400&h=500&fit=crop' },
                                { name: 'Bucket Hat', price: '\u20b9899', originalPrice: null, image: 'https://images.unsplash.com/photo-1556306535-0f09a537f0a3?w=400&h=500&fit=crop' },
                                { name: 'Chunky Sneakers', price: '\u20b94,499', originalPrice: '\u20b95,999', image: 'https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=400&h=500&fit=crop' },
                                { name: 'Mini Backpack', price: '\u20b91,799', originalPrice: null, image: 'https://images.unsplash.com/photo-1553062407-98eeb64c6a62?w=400&h=500&fit=crop' },
                                { name: 'Layered Necklace', price: '\u20b9799', originalPrice: '\u20b91,199', image: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=400&h=500&fit=crop' },
                                { name: 'Track Pants', price: '\u20b92,499', originalPrice: null, image: 'https://images.unsplash.com/photo-1552902865-b72c031ac5ea?w=400&h=500&fit=crop' }
                            ].map((product, index) => (
                                <div key={index} onClick={() => navigate('/template5/shop')} className="group cursor-pointer">
                                    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-4 bg-zinc-900">
                                        <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                                        {product.originalPrice && (
                                            <div className="absolute top-3 left-3 bg-fuchsia-500 text-white text-xs font-bold px-2 py-1 rounded-full">SALE</div>
                                        )}
                                        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                            <button className="w-full bg-white text-black py-3 font-bold text-sm uppercase tracking-wide hover:bg-fuchsia-500 hover:text-white transition-colors rounded-xl">
                                                Quick Add
                                            </button>
                                        </div>
                                    </div>
                                    <h3 className="font-bold text-white mb-1 group-hover:text-fuchsia-400 transition-colors">{product.name}</h3>
                                    <div className="flex items-center gap-2">
                                        <span className="font-bold text-white">{product.price}</span>
                                        {product.originalPrice && (
                                            <span className="text-white/40 line-through text-sm">{product.originalPrice}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="text-center mt-12">
                            <button
                                onClick={() => navigate('/template5/shop')}
                                className="inline-flex items-center gap-3 bg-gradient-to-r from-fuchsia-600 to-violet-600 text-white px-10 py-4 font-bold uppercase tracking-wide hover:from-fuchsia-500 hover:to-violet-500 transition-all duration-300 rounded-full"
                            >
                                View All Products
                                <ArrowRight className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </section>

                {/* UGC / INSTAGRAM - Desktop */}
                <section className="py-16 sm:py-20 bg-black">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <h2 className="text-3xl sm:text-4xl font-black uppercase mb-4">#MotionStyle</h2>
                            <p className="text-white/60">Tag us to get featured</p>
                        </div>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                            {[
                                'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&h=300&fit=crop',
                                'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop',
                                'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=300&h=300&fit=crop',
                                'https://images.unsplash.com/photo-1517841905240-472988babdf9?w=300&h=300&fit=crop',
                                'https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=300&h=300&fit=crop',
                                'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=300&h=300&fit=crop'
                            ].map((image, index) => (
                                <div key={index} className="relative aspect-square overflow-hidden group cursor-pointer">
                                    <img src={image} alt={`UGC ${index + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                    <div className="absolute inset-0 bg-fuchsia-600/0 group-hover:bg-fuchsia-600/40 transition-colors flex items-center justify-center">
                                        <div className="flex items-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <span className="flex items-center gap-1 text-white"><Heart className="w-5 h-5" fill="white" /></span>
                                            <span className="flex items-center gap-1 text-white"><MessageCircle className="w-5 h-5" /></span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* NEWSLETTER - Desktop */}
                <section className="py-16 sm:py-20 bg-gradient-to-r from-violet-600 via-fuchsia-600 to-pink-600">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                            <Sparkles className="w-4 h-4" />
                            <span className="text-sm font-medium">Join the Movement</span>
                        </div>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase mb-4">Get 15% Off</h2>
                        <p className="text-white/90 text-lg mb-8">Plus early access to drops, exclusive content, and more.</p>
                        <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                            <input
                                type="email"
                                placeholder="your@email.com"
                                className="flex-1 px-6 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-full text-white placeholder-white/60 focus:outline-none focus:border-white transition-colors"
                            />
                            <button type="submit" className="px-8 py-4 bg-white text-fuchsia-600 font-bold uppercase tracking-wide rounded-full hover:bg-black hover:text-white transition-all duration-300">
                                Subscribe
                            </button>
                        </form>
                        <p className="text-white/60 text-sm mt-4">No spam. Unsubscribe anytime.</p>
                    </div>
                </section>

                {/* SOCIAL LINKS - Desktop */}
                <section className="py-12 bg-black border-t border-zinc-800">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                            <p className="text-white/60 text-sm">Follow us for daily inspo</p>
                            <div className="flex items-center gap-4">
                                {['Instagram', 'TikTok', 'YouTube', 'Twitter'].map((social) => (
                                    <button key={social} className="px-4 py-2 bg-zinc-900 rounded-full text-sm font-medium text-white/80 hover:bg-fuchsia-600 hover:text-white transition-colors">
                                        {social}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

            </div>

            {/* Marquee animation styles */}
            <style jsx>{`
                @keyframes marquee {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                .animate-marquee {
                    animation: marquee 20s linear infinite;
                }
            `}</style>

        </div>
    );
};

export default Template5;
